import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { AddressSearchInput } from '@domgen/base-design-system-2.0/molecules/AddressSearchInput';
import { AddressSearchResult } from '@domgen/shared/models/services/api/getAddresses';
import {
  addressQueryResponseBuilder,
  AddressSearchResultWithSubBuildings,
} from '@domgen/shared/utils/address/addressQueryResponseBuilder';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from '../../../assets/icons/ChevronDown';
import { getAddresses } from '../../../services/api/getAddresses';
import { FormChangeEvent } from '../../../hooks/useFormValidation';
import { gaEvent } from '@domgen/shared/services/ga/ga';
import { usePostHog } from '@posthog/react';
import { ManualAddressForm } from './ManualAddressForm';
import { ManualMerchandiseLocationForm } from './ManualMerchandiseLocationForm';
import { FormKey } from '../../../enums/formKey';
import { StepKey } from '../../../enums/stepKey';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { store } from '../../../store/store';
import { ApplianceFormData } from '../../../models/form/appliance';

export interface AddressFormData {
  [FormKey.ADDRESS_LINE_1]: string;
  [FormKey.SUB_BUILDING]: string;
  [FormKey.STREET_NUMBER]: string;
  [FormKey.STREET]: string;
  [FormKey.PO_BOX]: string;
  [FormKey.CITY]: string;
  [FormKey.STATE_CODE]: string;
  [FormKey.POSTAL_CODE]: string;
  [FormKey.ADDRESS_ID]: string;
  isValid?: boolean;
  id: string;
  address: string;
}

export interface MerchandiseLocationFormData {
  [FormKey.MERCHANDISE_ADDRESS_LINE_1]: string;
  [FormKey.MERCHANDISE_SUB_BUILDING]: string;
  [FormKey.MERCHANDISE_STREET_NUMBER]: string;
  [FormKey.MERCHANDISE_STREET]: string;
  [FormKey.MERCHANDISE_PO_BOX]: string;
  [FormKey.MERCHANDISE_CITY]: string;
  [FormKey.MERCHANDISE_STATE_CODE]: string;
  [FormKey.MERCHANDISE_POSTAL_CODE]: string;
  [FormKey.MERCHANDISE_ADDRESS_ID]: string;
  isValid?: boolean;
  id: string;
}

interface AddressFormProps {
  formValues: Partial<ApplianceFormData[StepKey.PERSONAL_DETAILS]>;
  errors: Record<string, string>;
  onAddressSelect: (address: AddressSearchResult, addressType?: 'mailing' | 'merchandise') => void;
  handleFormChange: (e: FormChangeEvent) => string | void;
  onBlur: (e: FormChangeEvent) => string | void;
  addressType?: 'mailing' | 'merchandise';
  isMailingAddressPoBox?: boolean;
  applianceCategory: string;
  sessionId: string;
  systemCode: string;
}

export function AddressForm(props: AddressFormProps) {
  const { t } = useTranslation('personalDetailsPage', { keyPrefix: 'addressForm' });
  const {
    formValues,
    errors,
    onAddressSelect,
    handleFormChange,
    onBlur,
    addressType,
    isMailingAddressPoBox,
    applianceCategory,
    sessionId,
    systemCode,
  } = props;
  const [addressSearchResults, setAddressSearchResults] = useState<
    AddressSearchResultWithSubBuildings[] | undefined
  >(undefined);
  const [manualAddressEntry, setManualAddressEntry] = useState(false);
  const posthog = usePostHog();

  const handleManualAddressEntry = () => {
    setManualAddressEntry((prev) => !prev);
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'Manual address',
      eventLabel: 'CTA',
      type: 'STANDARD',
      pagename: addressType === 'merchandise' ? 'appliance-location' : 'personal-details',
      category: 'registration-pages',
      applianceCategory,
      appSessionID: sessionId,
      regSystemCode: systemCode,
    });
  };

  const handleAddressSearchSelect = useCallback(
    (address: AddressSearchResult) => {
      if (address) {
        gaEvent({
          eventLabel: 'COMPLETE',
          eventAction: 'customerAddress Auto',
          event: 'genericGAEvent',
          eventCategory: 'appliance details form USA',
          type: 'STANDARD',
          pagename: addressType === 'merchandise' ? 'appliance-location' : 'personal-details',
          category: 'registration-pages',
          applianceCategory,
          appSessionID: sessionId,
          regSystemCode: systemCode,
        });
      }

      setManualAddressEntry(true);
      onAddressSelect(address, addressType);

      setTimeout(() => {
        if (addressType === 'merchandise') {
          const merchandiseFieldMap: Record<string, keyof AddressSearchResult> = {
            merchandiseLocationLine1: 'addressLine1',
            merchandiseLocationSubBuilding: 'subBuilding',
            merchandiseLocationCity: 'city',
            merchandiseLocationRegionOrStateCode: 'regionOrStateCode',
            merchandiseLocationPostalCode: 'postalCode',
          };
          Object.entries(merchandiseFieldMap).forEach(([formField, resultField]) => {
            const value = address[resultField];
            if (value && typeof value !== 'object') {
              handleFormChange({
                target: { name: formField, value: String(value) },
              });
            }
          });
        }
        if (addressType === 'mailing') {
          ['addressLine1', 'subBuilding', 'city', 'regionOrStateCode', 'postalCode'].forEach(
            (field) => {
              const value = address[field as keyof AddressSearchResult];
              if (value && typeof value !== 'object') {
                handleFormChange({
                  target: {
                    name: field,
                    value: String(value),
                  },
                });
              }
            },
          );
        }
      });
    },
    [onAddressSelect, handleFormChange, addressType],
  );

  // Set manual address entry open if any of the relevant fields are already filled or if there are errors
  useEffect(() => {
    // Check if there are errors for the current address type
    const hasErrors = Object.keys(errors).some((field) => {
      if (addressType === 'merchandise') {
        return field.startsWith('merchandiseLocation');
      }
      if (addressType === 'mailing') {
        return ['addressLine1', 'subBuilding', 'city', 'regionOrStateCode', 'postalCode'].includes(
          field,
        );
      }
      return false;
    });

    if (hasErrors) {
      setManualAddressEntry(true);
      return;
    }

    // Check if any relevant fields are already filled
    for (const fields in formValues) {
      if (
        (addressType === 'merchandise' && fields === 'merchandiseLocationLine1') ||
        fields === 'merchandiseLocationCity' ||
        fields === 'merchandiseLocationRegionOrStateCode' ||
        fields === 'merchandiseLocationPostalCode' ||
        (addressType === 'mailing' &&
          (fields === 'addressLine1' ||
            fields === 'subBuilding' ||
            fields === 'city' ||
            fields === 'regionOrStateCode' ||
            fields === 'postalCode'))
      ) {
        if (formValues[fields as keyof typeof formValues] !== '') {
          setManualAddressEntry(true);
          return;
        }
      }
    }
  }, [formValues, errors, addressType]);

  const handleSearch = useCallback(
    async (query: string) => {
      const addresses = await getAddresses(query);
      const results = addressQueryResponseBuilder(addresses);
      setAddressSearchResults(results);
    },
    [addressType, applianceCategory, sessionId, systemCode],
  );

  return (
    <>
      <div className="col-span-8">
        <AddressSearchInput
          label={t('searchLabel')}
          placeholder={t('searchPlaceholder')}
          addressSearchResult={addressSearchResults}
          onAddressSearch={handleSearch}
          onAddressSearchSelect={handleAddressSearchSelect}
        />
      </div>
      <div className="col-span-8">
        <TextLink
          rightIcon={<ChevronDown className={`fill-primary-600`} isOpen={manualAddressEntry} />}
          onClick={() => {
            handleManualAddressEntry();
            capturePostHogEvent(
              posthog,
              `${addressType === 'mailing' ? 'personal-details' : 'appliance-location'}-manual-clicked`,
              store.get(),
            );
          }}
        >
          {t('enterManually')}
        </TextLink>
      </div>
      {manualAddressEntry &&
        (addressType === 'mailing' ? (
          <ManualAddressForm
            formValues={formValues}
            errors={errors}
            handleChange={handleFormChange}
            onBlur={onBlur}
            isMailingAddressPoBox={isMailingAddressPoBox}
          />
        ) : (
          <ManualMerchandiseLocationForm
            formValues={formValues}
            errors={errors}
            handleChange={handleFormChange}
            onBlur={onBlur}
          />
        ))}
    </>
  );
}
